import {Component, OnInit, signal, inject, DestroyRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {UserGroupsService} from '../../../services/http/user/user-groups.service';
import {ToastrService} from 'ngx-toastr';
import {UserGroupsFormComponent} from './user-groups-form/user-groups-form.component';
import {UserGroupsDeleteComponent} from './user-groups-delete/user-groups-delete.component';
import {UserGroup} from '../../../classes/models/user/UserGroup';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {StoreSummary} from '../../../classes/models/store/StoreSummary';
import {DataService} from '../../../services/store/data.service';
import {AccessRules} from '../../../classes/enums/Authority';
import {SHARED_IMPORTS} from '../../../shared/standalone-imports';

/**
 * This component handles displaying and editing user groups and their permissions.
 */
@Component({
  selector: 'app-user-groups',
  templateUrl: './user-groups.component.html',
  styleUrls: ['./user-groups.component.less'],
  imports: [SHARED_IMPORTS]
})
export class UserGroupsComponent implements OnInit {
  private readonly userGroupsService = inject(UserGroupsService);
  private readonly toastr = inject(ToastrService);
  private readonly modalService = inject(NgbModal);
  private readonly dataService = inject(DataService);
  private readonly destroyRef = inject(DestroyRef);

  /** A list of all user groups. */
  readonly userGroups = signal<UserGroup[]>(undefined!);
  /** Assigning a UserRole, so it could be used in HTML. */
  UserRole = AccessRules;
  /** `True` if a request sending to backend is in progress. */
  readonly loading = signal(false);
  /** The store, which is currently selected. */
  readonly selectedStore = signal<StoreSummary>(undefined!);
  /** The user group, which is currently selected for editing. */
  readonly editableGroup = signal<UserGroup | null>(null);

  /**
   * Retrieves user groups on page initialization.
   */
  ngOnInit(): void {
    this.getUserGroups();
    this.selectedStore.set(this.dataService.selectedStore);
  }

  private getUserGroups(): void {
    this.userGroupsService.getUserGroups()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: userGroups => {
                this.userGroups.set(userGroups);
              }
            });
  }

  /**
   * Sends a request to backend to update user group access.
   */
  onSubmit(): void {
    this.loading.set(true);
    this.userGroupsService.updateUserGroupAccess(this.editableGroup()!)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.loading.set(false);
                this.toastr.success('User group access has been successfully edited');
              },
              error: error => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              },
              complete: () => {
                this.getUserGroups();
                this.stopEditing();
              }
            });
  }

  /**
   * Checks if a user group is currently disabled for editing (only one user group can be edited at once).
   * @param userGroup a user group.
   */
  isDisabled(userGroup: UserGroup): boolean {
    return !this.editableGroup() || this.editableGroup()!.email !== userGroup.email;
  }

  /**
   * Marks a user group to be enabled for editing.
   * @param userGroup a user group, which is being edited.
   */
  startEditing(userGroup: UserGroup): void {
    this.editableGroup.set(userGroup);
  }

  /**
   * Stops allowing to edit a user group.
   */
  stopEditing(): void {
    this.editableGroup.set(null);
  }

  /**
   * Checks if a user group is marked as having access to a specific user role.
   * @param userGroup a user group.
   * @param userRole a user role, which access is being checked.
   */
  hasAccess(userGroup: UserGroup, userRole: AccessRules): boolean {
    return userGroup.accessRules.includes(userRole);
  }

  /**
   * Adds or removes user role from a user group.
   * @param userGroup a user group, which is being edited.
   * @param userRole a user role, which will be added or removed from a user group.
   */
  toggleAccess(userGroup: UserGroup, userRole: AccessRules): void {
    const index = userGroup.accessRules.indexOf(userRole);
    if (index === -1) {
      userGroup.accessRules.push(userRole);
    } else {
      userGroup.accessRules.splice(index, 1);
    }
  }

  /**
   * Opens a modal window to create a new user group.
   */
  openCreate(): void {
    const modalRef = UserGroupsFormComponent.open(this.modalService);
    modalRef.result.then(() => {
      this.getUserGroups();
    });
  }

  /**
   * Opens a modal window to delete an existing user group.
   * @param userGroup a user group to be deleted.
   */
  openDelete(userGroup: UserGroup): void {
    const modalRef = UserGroupsDeleteComponent.open(this.modalService, {userGroup});
    modalRef.result.then(() => {
      this.getUserGroups();
    });
  }
}